import { useId, useRef, useState } from 'react'
import { Button, Label, Spinner } from '@heroui/react'

import { AlertIcon, PlusIcon, TrashIcon } from '@/components/ui/icons'
import type { FileRef } from '@/lib/api/common'
import { errorMessage } from '@/lib/api/api-error'

import { useFileUpload } from '../hooks'
import { imageFileError } from '../image-validation'

interface ImageGalleryFieldProps {
  label: string
  description?: string
  /** The images attached, in the order they are shown. */
  value: FileRef[]
  onChange: (files: FileRef[]) => void
  isDisabled?: boolean
  /** Stops the picker offering more once the gallery is this long. */
  maxImages?: number
  isLabelHidden?: boolean
}

/**
 * Several images, each uploaded the moment it is picked — `ImageField`'s idea, many times.
 *
 * Files are uploaded one after another rather than together: the hook runs a single
 * mutation, and a merchant adding a handful of photos is better served by the
 * first appearing straight away than by all of them landing at once. A file that
 * fails is reported and skipped; the ones before it stay.
 */
export function ImageGalleryField({
  label,
  description,
  value,
  onChange,
  isDisabled,
  maxImages = 8,
  isLabelHidden,
}: ImageGalleryFieldProps) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const { upload, isUploading, error } = useFileUpload()
  const [localError, setLocalError] = useState<string | null>(null)

  const remaining = maxImages - value.length

  async function handlePick(files: FileList | null) {
    if (!files || files.length === 0) return

    setLocalError(null)

    const picked = [...files].slice(0, Math.max(remaining, 0))
    const added: FileRef[] = []

    if (files.length > picked.length) {
      setLocalError(`Only ${maxImages} images fit — the rest were left out.`)
    }

    try {
      for (const file of picked) {
        const invalid = imageFileError(file)

        if (invalid) {
          setLocalError(invalid)
          continue
        }

        added.push(await upload(file))
      }
    } catch {
      // `error` off the mutation carries the server's wording; nothing to add.
    } finally {
      // Whatever uploaded before a failure is kept rather than thrown away.
      if (added.length > 0) onChange([...value, ...added])
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const message = localError ?? (error ? errorMessage(error, 'Upload failed.') : null)

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId} className={isLabelHidden ? 'sr-only' : undefined}>
        {label}
      </Label>

      <div className="flex flex-wrap gap-3">
        {value.map((file, index) => (
          <div
            key={file.id}
            className="border-border bg-surface group relative size-20 overflow-hidden rounded-xl border"
          >
            <img src={file.url} alt="" className="size-full object-cover" />

            <Button
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label={`Remove image ${index + 1}`}
              isDisabled={isDisabled || isUploading}
              onPress={() => onChange(value.filter((item) => item.id !== file.id))}
              className="bg-background/80 text-muted hover:text-danger absolute end-1 top-1 size-6 min-w-0 rounded-full"
            >
              <TrashIcon width={12} height={12} />
            </Button>
          </div>
        ))}

        {isUploading && (
          <div className="border-border bg-surface flex size-20 items-center justify-center rounded-xl border">
            <Spinner size="sm" />
          </div>
        )}

        {remaining > 0 && (
          <>
            <input
              ref={inputRef}
              id={inputId}
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              disabled={isDisabled || isUploading}
              onChange={(event) => void handlePick(event.target.files)}
            />

            <Button
              variant="ghost"
              isDisabled={isDisabled || isUploading}
              onPress={() => inputRef.current?.click()}
              className="border-border text-muted size-20 flex-col gap-1 rounded-xl border border-dashed text-[11px]"
            >
              <PlusIcon width={16} height={16} />
              Add
            </Button>
          </>
        )}
      </div>

      {description && !message && <p className="text-muted text-xs">{description}</p>}

      {message && (
        <p role="alert" className="text-danger flex items-center gap-1 text-xs">
          <AlertIcon width={13} height={13} />
          {message}
        </p>
      )}
    </div>
  )
}
